import { Settings } from 'lucide-react';
import SolutionPage from '../../components/SolutionPage';

export default function BusinessWorkflow() {
  return (
    <SolutionPage
      label="Solution"
      headline="Repetitive work should not require the same effort every time."
      subheadline="When the same tasks have to be done manually, repeatedly, they accumulate into a significant drain on time and attention, resources better spent on running the business."
      heroImage="https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1920"
      icon={Settings}
      problemTitle="Administrative repetition is an invisible cost that grows over time."
      problemBody={[
        'Most business owners can identify the work that needs doing. What is harder to see is how much of that work consists of the same actions, repeated over and over, each one consuming time, attention, and energy.',
        'Sending the same information to new clients. Following up on the same types of requests. Re-entering information from one place to another. Answering the same questions multiple times per week.',
        'Individually, these tasks seem minor. Collectively, they represent hours every week that could be spent on the work that actually requires human judgment and expertise.',
      ]}
      outcomes={[
        {
          title: 'Consistent task execution without manual effort',
          description:
            'Routine operational tasks run reliably in the background, producing the same quality result each time without requiring manual attention.',
        },
        {
          title: 'Fewer things falling through the gaps',
          description:
            'When processes are clearly defined, important steps do not get skipped during busy periods or when team members are absent.'
        },
        {
          title: 'More time for meaningful work',
          description:
            'Freeing your team from repetitive coordination means more capacity for the work that genuinely requires skill, judgment, and experience.',
        },
        {
          title: 'Greater operational consistency',
          description:
            'Customers and clients receive a consistent experience regardless of who on your team is handling a particular interaction.',
        },
      ]}
      whatWeDoTitle="We design operational workflows that run reliably without constant oversight."
      whatWeDoBody={[
        'We start by understanding which tasks in your business are repeated most often and which ones cause the most friction when they are not handled well.',
        'From there, we design workflows that handle routine work reliably, not through complicated software, but through clear, well-structured processes that are easy to maintain and adapt.',
        'The measure of a good workflow is not how sophisticated it is. It is how much less effort is required to achieve the same or better results.'
      ]}
      whatYouGain={[
        'Reduced time spent on repetitive administrative tasks each week.',
        'Consistent execution of operational processes without manual reminders.',
        'More capacity for your team to focus on high-value work.',
        'An operation that runs smoothly even during busy or challenging periods.',
        'Greater confidence that important tasks are not being overlooked.',
        'A business that feels more organized and less reliant on individual memory.',
      ]}
      relatedSolutions={[
        { title: 'Internal Organization', href: '/solutions/internal-organization' },
        { title: 'Customer Communication', href: '/solutions/customer-communication' },
        { title: 'Lead Qualification', href: '/solutions/lead-qualification' },
      ]}
    />
  );
}
